import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef } from "react";
import { Zap } from "lucide-react";

/**
 * Early Momentum Modal - 10 Tasks
 * 
 * Quiet acknowledgment, encouraging, low intensity.
 * Not celebratory - just supportive.
 */

// Very light haptic tap
const triggerLightHaptic = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate(25);
  }
};

export const EarlyMomentumModal = ({ isOpen, onClose }) => {
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    if (isOpen && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      // Light haptic after modal appears
      setTimeout(() => {
        triggerLightHaptic();
      }, 250);
    }
    
    if (!isOpen) {
      hasTriggeredRef.current = false;
    }
  }, [isOpen]);

  const handleDismiss = () => {
    triggerLightHaptic();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - very soft dim */}
          <motion.div
            className="fixed inset-0 bg-black/20 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative bg-[#FFFCF9] rounded-2xl p-7 shadow-[0_8px_30px_rgba(0,0,0,0.08)] max-w-xs mx-4 pointer-events-auto"
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Subtle icon - reduced intensity */}
              <div className="flex justify-center mb-5">
                <motion.div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.12) 0%, rgba(234, 88, 12, 0.08) 100%)',
                  }}
                  initial={{ scale: 0.9, opacity: 0.8 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <Zap 
                    className="text-orange-400" 
                    size={22} 
                    strokeWidth={2} 
                    style={{ opacity: 0.85 }}
                  />
                </motion.div>
              </div>

              {/* Text content - minimal */}
              <div className="text-center">
                <motion.h2
                  className="text-xl font-semibold text-[#2D2D2D] mb-1"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.1 }}
                >
                  Nice Start
                </motion.h2>
                
                <motion.p
                  className="text-sm text-[#7A7A7A] mb-2"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.15 }}
                >
                  10 tasks completed
                </motion.p>
                
                <motion.p
                  className="text-sm text-[#9A9A9A] mb-5"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.2 }}
                >
                  You're finding your rhythm.
                </motion.p>

                {/* Simple button - less gradient intensity */}
                <motion.button
                  className="px-6 py-2.5 rounded-full text-white font-medium text-sm"
                  style={{
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                    opacity: 0.9,
                  }}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 0.9, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.25 }}
                  whileHover={{ opacity: 1 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDismiss}
                  data-testid="early-momentum-continue-btn"
                >
                  Keep going
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EarlyMomentumModal;
